import WorkflowRunButton from './WorkflowRunButton';
// 261010 Epic 2 / P2a + P2b + P8c — nội dung từng bước ở trang chi tiết video (khung mới). Không có `interactive` ⇒ chỉ đọc; có ⇒ Kịch bản / Đạo diễn / Quay phim / Sơ đồ / Storyboard / Prompt / Video / Ghép có thao tác.
import React from 'react';
import { Spin } from 'antd';
import type { EventPlan, EventPlanScene, FilmEvent, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { WorkflowStepKey } from '../../types/filmWorkflow';
import WorkflowMapStep from './WorkflowMapStep';
import WorkflowStoryboardStep from './WorkflowStoryboardStep';
import StoryboardCaptions from './StoryboardCaptions';
import WorkflowPromptStep from './WorkflowPromptStep';
import WorkflowVideoStep from './WorkflowVideoStep';
import WorkflowAssemblyStep from './WorkflowAssemblyStep';
import WorkflowAssetsTabs from './WorkflowAssetsTabs'; // 261010 P4b-2
import WorkflowScriptStep, { ScriptView } from './WorkflowScriptStep';
import WorkflowDirectingStep, { DirectingView } from './WorkflowDirectingStep';
import WorkflowCinematographyStep, { CinematographyView } from './WorkflowCinematographyStep';
import type { InteractiveProps } from './WorkflowStepShared';
import { formatClock, truncateText } from './workflowBoardUtils';
import {
  approvalLabel, clipStatusLabel, driftLabel, mapsOfEvent, selectedClipOf, stalenessLabel,
} from './workflowDetailUtils';

export interface StepContentProps {
  step: WorkflowStepKey;
  eventId: string;
  ev: FilmEvent | null;
  scene: EventPlanScene | null;
  detail: EventPlan | null;
  storyboards: StoryboardListItem[] | null;
  maps: PositionMapsResponse | null;
  /** Lỗi nạp nguồn phụ (storyboard / sơ đồ) — hiện đúng ở bước dùng nó. */
  storyboardsError: string | null;
  mapsError: string | null;
  /** 261010 P2b-1 + P2b-2 + P2b-3 — có ⇒ bảy bước Kịch bản / Đạo diễn (sửa tay) / Sơ đồ / Storyboard / Prompt / Video / Ghép có THAO TÁC (theo `workflowActionPolicy`); không có ⇒ chỉ đọc như P2a. */
  interactive?: InteractiveProps;
}

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="muted" style={{ margin: '6px 0' }}>{children}</p>;
const Img: React.FC<{ src?: string | null; alt: string }> = ({ src, alt }) => (src ? <img className="wf-img" src={src} alt={alt} loading="lazy" /> : null);

const WorkflowStepContent: React.FC<StepContentProps> = ({ step, eventId, ev, scene, detail, storyboards, maps, storyboardsError, mapsError, interactive }) => {
  if (step === 'ASSEMBLY') {
    if (interactive) return <WorkflowAssemblyStep {...interactive} detail={detail} />;
    const url = detail?.plan?.exportUrl;
    return (
      <div>
        <Empty>Ghép tập là việc cấp plan: gom các video đã chọn thành một tập.</Empty>
        {url ? <video src={url} controls preload="none" style={{ width: '100%', maxWidth: 420, borderRadius: 10, background: '#000' }} /> : <Empty>Chưa có bản ghép.</Empty>}
        {detail?.plan?.exportStale ? <p className="wf-alert WARN">Bản ghép đã cũ — có thay đổi sau lần ghép gần nhất.</p> : null}
      </div>
    );
  }
  if (!ev) return <Empty>Chưa tải được dữ liệu chi tiết của video này — thử tải lại.</Empty>;
  const phases = ev.videoMeta?.phases ?? [];

  switch (step) {
    case 'SCRIPT':
      if (interactive) return <WorkflowScriptStep key={eventId} {...interactive} eventId={eventId} ev={ev} detail={detail} />; // key = eventId: đổi video ⇒ bỏ trình sửa (trang đã hỏi "Bỏ thay đổi chưa lưu?")
      return <ScriptView ev={ev} />;
    case 'ASSETS':
      // 261010 P4b-2 — ba tab (Nhân vật theo video · Bối cảnh + Đạo cụ cấp tập). KHÔNG `key` theo eventId: đổi video giữ tab đang chọn và không nạp lại `location-needs`.
      // `planId` lấy từ `interactive` (luôn có ở trang thật); không có (chỉ đọc cũ) thì không dựng được tab Bối cảnh.
      if (interactive) return <WorkflowAssetsTabs planId={interactive.planId} storyId={interactive.storyId} ev={ev} detail={detail} jobs={interactive.jobs} onActionDone={interactive.onActionDone} />;
      return <Empty>Bước Tài sản cần mở từ trang plan của khung mới.</Empty>;
    case 'DIRECTING':
      if (interactive) return <WorkflowDirectingStep key={eventId} {...interactive} eventId={eventId} ev={ev} detail={detail} />;
      return <DirectingView ev={ev} />;
    case 'CINEMATOGRAPHY':
      if (interactive) return <WorkflowCinematographyStep key={eventId} {...interactive} eventId={eventId} ev={ev} detail={detail} />;
      return <CinematographyView ev={ev} />;
    case 'MAP': {
      if (interactive) return <WorkflowMapStep {...interactive} eventId={eventId} ev={ev} scenes={detail?.scenes ?? []} maps={maps} mapsError={mapsError} />;
      if (mapsError) return <p className="wf-alert ERROR">Không tải được sơ đồ: {mapsError}</p>;
      if (!maps) return <Spin size="small" />;
      const rows = mapsOfEvent(maps, eventId, phases.map((p) => p.order));
      return (
        <ul className="wf-list">{rows.map((r) => (
          <li key={String(r.phaseOrder)}><span className="k">{r.phaseOrder === null ? 'Cả video' : `GĐ${r.phaseOrder}`}</span>
            {r.map ? (<>
              <b>{r.map.name}</b> <span className="muted">— {r.map.imageUrl ? `${approvalLabel(r.map.approval?.state)} · ${stalenessLabel(r.map.staleness)}` : 'chưa có ảnh'}</span>
              <Img src={r.map.imageUrl} alt={`Sơ đồ ${r.map.name}`} />
            </>) : <span className="muted">chưa liên kết sơ đồ</span>}
          </li>))}
        </ul>
      );
    }
    case 'STORYBOARD': {
      if (interactive) return <><WorkflowRunButton planId={detail!.plan.id} step="STORYBOARD" eventId={eventId} onDone={interactive.onActionDone}/><WorkflowStoryboardStep {...interactive} eventId={eventId} ev={ev} storyboards={storyboards} storyboardsError={storyboardsError} maps={maps} /></>;
      if (storyboardsError) return <p className="wf-alert ERROR">Không tải được storyboard: {storyboardsError}</p>;
      if (!storyboards) return <Spin size="small" />;
      const sb = storyboards.find((s) => s.eventId === eventId)?.storyboard;
      if (!sb) return <Empty>Chưa có storyboard cho video này.</Empty>;
      const url = sb.sheetUrl || sb.sheetRawUrl;
      return (
        <div>
          <div className="wf-kv"><span>{sb.frames.length} khung · {sb.groups.length} nhóm</span><span>Duyệt: <b>{approvalLabel(sb.approval?.state)}</b></span><span>Độ mới: <b>{stalenessLabel(sb.staleness)}</b></span></div>
          {url ? <Img src={url} alt="Tấm storyboard" /> : <Empty>Mới có khung, chưa có tấm ảnh.</Empty>}
          <StoryboardCaptions frames={sb.frames} shots={ev.shots} />
          {sb.drift.length > 0 && (<><h3>Lệch dữ liệu hiện tại</h3><ul className="wf-list">{sb.drift.map((d, i) => <li key={i}><span className="k">Khung {d.frameNo}</span>{driftLabel(d.reason)}</li>)}</ul></>)}
        </div>
      );
    }
    case 'PROMPT': {
      if (interactive) return <WorkflowPromptStep {...interactive} eventId={eventId} ev={ev} detail={detail} />;
      const w = (ev.promptWarnings || []).filter(Boolean);
      return (
        <div>
          <div className="wf-kv"><span>Kiểu prompt: <b>{ev.effectivePromptMode === 'NATURAL' ? 'Tự nhiên' : ev.effectivePromptMode === 'TECHNICAL' ? 'Kỹ thuật' : '—'}</b></span>
            <span>Tình trạng: <b>{ev.promptModeState === 'OK' ? 'khớp kiểu hiện hành' : ev.promptModeState === 'MISMATCH' ? 'lệch kiểu hiện hành' : 'chưa có prompt'}</b></span></div>
          {ev.authorPrompt ? <pre className="wf-pre" tabIndex={0} aria-label="Prompt đang lưu">{ev.authorPrompt}</pre> : <Empty>Chưa có prompt.</Empty>}
          {w.length > 0 && (<><h3>Cảnh báo lúc dựng prompt</h3><ul className="wf-list">{w.map((x, i) => <li key={i}>{truncateText(x, 400)}</li>)}</ul></>)}
        </div>
      );
    }
    case 'VIDEO': {
      if (interactive) return <><WorkflowRunButton planId={detail!.plan.id} step="VIDEO" eventId={eventId} onDone={interactive.onActionDone}/><WorkflowVideoStep key={eventId} {...interactive} eventId={eventId} ev={ev} detail={detail} workflowManaged /> </>; // key = eventId: đổi video ⇒ tuỳ chọn gen về mặc định (không mang model / số bản sang video khác)
      const clips = ev.clips || [];
      const sel = selectedClipOf(ev);
      return (
        <div>
          <div className="wf-kv"><span>Engine: <b>{ev.videoEngine === 'SEEDANCE_25' ? 'Seedance 2.5' : ev.videoEngine === 'SEEDANCE_20' ? 'Seedance 2.0' : '—'}</b></span><span>{clips.length} bản clip</span><span>Bản được chọn: <b>{sel ? `#${clips.findIndex((c) => c.id === sel.id) + 1}` : 'chưa chọn'}</b></span></div>
          {clips.length === 0 ? <Empty>Chưa có clip nào.</Empty> : (
            <div className="wf-clips">{clips.map((c, i) => (
              <div key={c.id}>
                <div className="wf-kv"><b>Bản {i + 1}{sel?.id === c.id ? ' · đang chọn' : ''}</b><span>{clipStatusLabel(c.status)}</span>{c.durationS ? <span className="mono">{formatClock(c.durationS)}</span> : null}</div>
                {c.status === 'DONE' && c.videoUrl ? <video src={c.videoUrl} controls preload="none" /> : null}
                {c.status === 'FAILED' && c.error ? <p className="wf-alert ERROR">{truncateText(c.error, 300)}</p> : null}
              </div>))}
            </div>)}
          <p className="wf-note" style={{ marginTop: 10 }}>"Có kết quả" ở bước này nghĩa là đã chọn một bản clip xong. Hệ thống chưa lưu việc người duyệt clip nên bước này không hiện "đã duyệt".</p>
        </div>
      );
    }
    default:
      return <Empty>Bước này chưa có nội dung ở bản này.</Empty>;
  }
};

export default WorkflowStepContent;
